import { useEffect, useRef, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Field } from '@/components/ui/Field';
import { Input } from '@/components/ui/Input';
import { MessageBar } from '@/components/ui/MessageBar';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/auth';

interface MagicLinkResponse {
  ok: boolean;
  token: string;
  expiresAt: string;
}

interface PollResponse {
  ready: boolean;
  jwt?: string;
}

interface VerifyResponse {
  jwt: string;
}

export function LoginPage() {
  const { signIn } = useAuth();

  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [phase, setPhase] = useState<'enter_email' | 'awaiting' | 'verifying'>('enter_email');
  const [pollToken, setPollToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const pollerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (pollerRef.current != null) window.clearInterval(pollerRef.current);
    };
  }, []);

  const startPolling = (token: string): void => {
    if (pollerRef.current != null) window.clearInterval(pollerRef.current);
    pollerRef.current = window.setInterval(async () => {
      try {
        const res = await api<PollResponse>('/api/auth/poll', {
          method: 'POST',
          unauthenticated: true,
          allowUnauthorized: true,
          body: { token },
        });
        if (res.ready && res.jwt) {
          if (pollerRef.current != null) window.clearInterval(pollerRef.current);
          pollerRef.current = null;
          await signIn(res.jwt);
        }
      } catch (err) {
        if (err instanceof ApiError && err.status === 410) {
          if (pollerRef.current != null) window.clearInterval(pollerRef.current);
          pollerRef.current = null;
          setError('Sign-in attempt expired. Request a new link.');
          setPhase('enter_email');
        }
      }
    }, 2_000);
  };

  const requestMagicLink = async (): Promise<void> => {
    setError(null);
    setBusy(true);
    try {
      const res = await api<MagicLinkResponse>('/api/auth/magic-link', {
        method: 'POST',
        unauthenticated: true,
        body: { email: email.trim() },
      });
      setPollToken(res.token);
      setPhase('awaiting');
      startPolling(res.token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send sign-in link.');
    } finally {
      setBusy(false);
    }
  };

  const verifyWithCode = async (): Promise<void> => {
    setError(null);
    setPhase('verifying');
    try {
      const res = await api<VerifyResponse>('/api/auth/verify', {
        method: 'POST',
        unauthenticated: true,
        body: { email: email.trim(), code: code.trim() },
      });
      await signIn(res.jwt);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid code.');
      setPhase('awaiting');
    }
  };

  return (
    <div className="grid min-h-screen place-items-center bg-brand-50 p-6">
      <div className="w-full max-w-md rounded-xl border border-brand-200 bg-white p-8 shadow-lg">
        <div className="mb-6 flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-900 text-base font-bold text-white">
            K
          </div>
          <div>
            <h1 className="text-lg font-semibold text-brand-900">Sign in to Kiro</h1>
            <p className="text-xs text-brand-500">app.getkiro.dev</p>
          </div>
        </div>

        {error && (
          <div className="mb-4">
            <MessageBar intent="error">{error}</MessageBar>
          </div>
        )}

        {phase === 'enter_email' && (
          <div className="flex flex-col gap-4">
            <Field label="Work email">
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && email.trim().length > 0 && !busy) void requestMagicLink();
                }}
              />
            </Field>
            <Button
              size="lg"
              loading={busy}
              disabled={email.trim().length === 0}
              onClick={() => void requestMagicLink()}
            >
              {busy ? 'Sending…' : 'Send sign-in link'}
            </Button>
            <p className="text-xs text-brand-500">
              We&rsquo;ll email you a link and a 6-digit code. Click the link from any device or
              paste the code below.
            </p>
          </div>
        )}

        {phase === 'awaiting' && (
          <div className="flex flex-col gap-4">
            <p className="text-sm text-brand-700">
              Check <span className="font-semibold">{email}</span>. Click the link in the email or
              paste the 6-digit code.
            </p>
            <Field label="Code">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="123456"
                inputMode="numeric"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && code.trim().length === 6) void verifyWithCode();
                }}
              />
            </Field>
            <div className="flex gap-2">
              <Button disabled={code.trim().length !== 6} onClick={() => void verifyWithCode()}>
                Verify code
              </Button>
              <Button
                variant="ghost"
                onClick={() => {
                  setPollToken(null);
                  setPhase('enter_email');
                  setCode('');
                }}
              >
                Use a different email
              </Button>
            </div>
            <p className="text-xs text-brand-500">
              Waiting for the link click… you don&rsquo;t need to refresh.
              {pollToken && <> Polling token #{pollToken.slice(0, 6)}…</>}
            </p>
          </div>
        )}

        {phase === 'verifying' && <p className="text-sm text-brand-700">Verifying…</p>}
      </div>
    </div>
  );
}
